import type { OidcPkceProvider } from "@p0security/shared/types/tenant/sso";
import { oidcAuthServerUrl } from "@p0security/shared/types/tenant/sso";
import { deferral } from "@p0security/util/deferral";
import type { Auth, OAuthProvider, UserCredential } from "firebase/auth";
import { signInWithCredential } from "firebase/auth";
import pkceChallenge from "pkce-challenge";
import { useLocation } from "react-router";

import AuthConst from "../../constants/AuthConst";
import { getEnvironment } from "../../utils/environment";
import { TOKEN_EXPIRATION_KEY } from "../Session/useSessionTimeout";

const rejectionReason = (body: string) => {
  try {
    // RFC 6749 §5.2 names these `error`/`error_description`; Okta answers with
    // `errorCode`/`errorSummary` instead.
    const { error, error_description, errorCode, errorSummary } =
      JSON.parse(body);
    const summary = error_description ?? errorSummary;
    const code = error ?? errorCode;
    if (summary && code) return `${summary} (${code})`;
    return summary ?? code ?? body;
  } catch {
    return body;
  }
};

/** Thrown when the authorization server refuses to redeem the authorization code.
 *
 * `message` is safe to show the user: P0 relays the authorization server's 4xx
 * verbatim, so its body carries the actual reason the sign-in failed
 * (ENG-8521).
 */
export class OidcTokenExchangeError extends Error {
  constructor(body: string) {
    super(
      `Your identity provider rejected this sign-in: ${rejectionReason(body)}`
    );
  }
}

// Mounted at oidc/auth/_redirect
export const OidcRedirect: React.FC<object> = () => {
  const { search } = useLocation();
  const { error } = window.opener.p0.relayPkce(new URLSearchParams(search));
  if (error) return <div>{error}</div>;
  window.close();
  return <></>;
};

export const signInWithPkce = async (
  auth: Auth,
  provider: OAuthProvider,
  tenant: OidcPkceProvider,
  orgSlug: string
): Promise<UserCredential> => {
  const codeDeferral = deferral<string>();

  const state = crypto.randomUUID();
  const base = oidcAuthServerUrl(tenant);
  const { code_challenge, code_verifier } = pkceChallenge();
  const redirect_uri = new URL(`oidc/auth/_redirect`, window.location.origin)
    .href;

  const data = {
    client_id: tenant.clientId,
    code_challenge,
    code_challenge_method: "S256",
    redirect_uri,
    response_type: "code",
    scope: "openid groups profile email",
    state,
  };
  const search = new URLSearchParams(data);

  (window as any).p0 = {
    relayPkce: (search: URLSearchParams) => {
      if (search.get("state") !== state) {
        codeDeferral.reject("Code exchange state mismatch");
        return { error: "Code exchange state mismatch" };
      }
      const code = search.get("code");
      if (code) {
        codeDeferral.resolve(code);
      } else {
        codeDeferral.reject("No code returned");
      }
      return { ok: true };
    },
  };
  const authorizePath =
    tenant.providerType === "cloudflare" ? "authorization" : "authorize";
  window.open(
    `${base}/${authorizePath}?${search.toString()}`,
    "p0_oidc_signin",
    "popup=true"
  );

  const code = await codeDeferral.promise;
  const { apiUrl } = getEnvironment();

  // TODO: Should be useApiFetch
  const response = await fetch(`${apiUrl}/o/${orgSlug}/oidc/token`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      client_id: data.client_id,
      code,
      code_verifier,
      grant_type: "authorization_code",
      redirect_uri: data.redirect_uri,
      scope: data.scope,
      ...(tenant.providerType === "cloudflare"
        ? { client_secret: tenant.clientSecret }
        : {}),
    }).toString(),
  });

  if (!response?.ok) {
    throw new OidcTokenExchangeError(await response.text());
  }

  const token = await response.json();

  // Extract expires_in (in seconds) and convert to milliseconds in UTC
  if (token.expires_in && typeof token.expires_in === "number") {
    const tokenExpiration = Date.now() + token.expires_in * 1000;
    const persistAuth =
      localStorage.getItem(AuthConst.PERSIST_AUTH_KEY) === "true";
    // Clear stale value from the other storage in case the persistAuth preference was toggled
    if (persistAuth) {
      sessionStorage.removeItem(TOKEN_EXPIRATION_KEY);
      localStorage.setItem(TOKEN_EXPIRATION_KEY, tokenExpiration.toString());
    } else {
      localStorage.removeItem(TOKEN_EXPIRATION_KEY);
      sessionStorage.setItem(TOKEN_EXPIRATION_KEY, tokenExpiration.toString());
    }
  }

  const credential = await signInWithCredential(
    auth,
    provider.credential({
      accessToken: token.access_token,
      idToken: token.id_token,
    })
  );
  return credential;
};
